import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../utils/api';
import { Wallet, ArrowLeft, CheckCircle, Lock } from 'lucide-react';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/auth/reset-password', {
        token,
        password,
        confirm_password: confirmPassword
      });
      setSuccess(true);
      toast.success(res.data.message);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(e => e.msg?.replace('Value error, ', '')).join('. '));
      } else {
        toast.error(detail || 'Failed to reset password');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-muted" data-testid="reset-password-page">
        <Card className="w-full max-w-md border border-border/50 shadow-xl">
          <CardContent className="pt-8 text-center space-y-4">
            <p className="text-destructive font-medium" data-testid="invalid-token-message">Invalid reset link. No token provided.</p>
            <Link to="/forgot-password">
              <Button variant="outline" className="rounded-full" data-testid="request-new-link-button">Request a new reset link</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-muted" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8" data-testid="back-to-login-link">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to login
        </Link>

        <Card className="border border-border/50 shadow-xl" data-testid="reset-password-card">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Wallet className="h-8 w-8 text-primary" />
              <span className="text-2xl font-bold text-primary" style={{fontFamily: 'DM Sans'}}>SamaFlux</span>
            </div>
            <CardTitle className="text-2xl" style={{fontFamily: 'DM Sans'}} data-testid="card-title">
              {success ? 'Password Reset!' : 'Set New Password'}
            </CardTitle>
            <CardDescription data-testid="card-description">
              {success ? 'Your password has been updated' : 'Enter your new password below'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {success ? (
              <div className="text-center space-y-4" data-testid="reset-success">
                <div className="flex justify-center">
                  <CheckCircle className="h-16 w-16 text-primary" />
                </div>
                <p className="text-muted-foreground">You can now log in with your new password.</p>
                <Button
                  className="w-full rounded-full bg-primary hover:bg-primary/90 payment-button"
                  onClick={() => navigate('/login')}
                  data-testid="go-to-login-button"
                >
                  Go to Login
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="Min. 8 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={8}
                      className="pl-10"
                      data-testid="password-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="Re-enter password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={8}
                      className="pl-10"
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full rounded-full bg-primary hover:bg-primary/90 payment-button"
                  disabled={loading}
                  data-testid="submit-button"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
